import { ArrowBack, ChevronRight, Save, MenuBook, Share, DifferenceOutlined } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getChapterShareUrl } from '@/lib/stories-url';

interface PublishEditorHeaderProps {
  chapterName: string;
  publishStatus: 'draft' | 'preview' | 'public';
  onStatusChange: (status: 'draft' | 'preview' | 'public') => Promise<void>;
  wordCount: number;
  readingTime: number;
  isSaving: boolean;
  isChangingStatus: boolean;
  isDirty: boolean;
  onSave: () => void;
  canUseAutosave: boolean;
  autosaveEnabled: boolean;
  onAutosaveToggle: (enabled: boolean) => void;
  mainContentId?: string;
  canPublish: boolean;
  publicationId?: string;
  chapterPublicId?: string;
  variantsOpen: boolean;
  onToggleVariants: () => void;
  isOutlineOpen: boolean;
  onToggleOutline: () => void;
}

export function PublishEditorHeader({
  chapterName,
  publishStatus,
  onStatusChange,
  wordCount,
  readingTime,
  isSaving,
  isChangingStatus,
  isDirty,
  onSave,
  canUseAutosave,
  autosaveEnabled,
  onAutosaveToggle,
  mainContentId,
  canPublish,
  publicationId,
  chapterPublicId,
  variantsOpen,
  onToggleVariants,
  isOutlineOpen,
  onToggleOutline,
}: PublishEditorHeaderProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'draft' | 'preview' | 'public' | null>(null);

  const handleStatusChange = async (event: SelectChangeEvent<'draft' | 'preview' | 'public'>) => {
    const status = event.target.value as 'draft' | 'preview' | 'public';
    setPendingStatus(status);
    try {
      await onStatusChange(status);
    } finally {
      setPendingStatus(null);
    }
  };

  const share = async (url: string, title: string) => {
    if (navigator.share) await navigator.share({ title, url });
    else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {!isOutlineOpen && (
          <Tooltip title={t('editor.outline')}>
            <IconButton onClick={onToggleOutline} size="small" color="inherit">
              <ChevronRight />
            </IconButton>
          </Tooltip>
        )}
        <IconButton component={Link} to="/scenes" size="small" color="inherit">
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBook color="primary" /> {chapterName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('chapters.publish.header_subtitle')}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {wordCount.toLocaleString()} {t('editor.words')} | {readingTime} min {t('editor.reading_time')}
        </Typography>

        <Tooltip title={t('chapters.publish.variants_title')}>
          <IconButton color={variantsOpen ? 'primary' : 'default'} onClick={onToggleVariants}><DifferenceOutlined /></IconButton>
        </Tooltip>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={publishStatus}
            onChange={handleStatusChange}
            onOpen={() => setStatusMenuOpen(true)}
            onClose={() => setStatusMenuOpen(false)}
            disabled={isChangingStatus}
            IconComponent={isChangingStatus ? () => <CircularProgress size={16} sx={{ mr: 1.5 }} /> : undefined}
            renderValue={(status) => {
              const label = t(`chapters.publish.status_${status}`);
              return statusMenuOpen
                ? <span>{label}</span>
                : <Tooltip title={t(`chapters.publish.status_${status}_help`)} arrow placement="top" disableInteractive><span>{label}</span></Tooltip>;
            }}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: '0.8rem' }}
          >
            <MenuItem value="draft">{t('chapters.publish.status_draft')}</MenuItem>
            <MenuItem value="preview">{t('chapters.publish.status_preview')}</MenuItem>
            <MenuItem value="public" disabled={!canPublish} title={!canPublish ? t('chapters.publish.owner_only') : undefined} sx={!canPublish ? { pointerEvents: 'auto' } : undefined}>{t('chapters.publish.status_public')}</MenuItem>
          </Select>
        </FormControl>
        {isChangingStatus && (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {t('chapters.publish.status_changing', {
              status: t(`chapters.publish.status_${pendingStatus || publishStatus}`)
            })}
          </Typography>
        )}

        {publishStatus === 'public' && publicationId && chapterPublicId && (
          <Tooltip title={copied ? t('chapters.publish.link_copied') : t('chapters.publish.share_chapter')}>
            <IconButton color="primary" onClick={() => share(getChapterShareUrl(publicationId, chapterPublicId), chapterName)}><Share /></IconButton>
          </Tooltip>
        )}

        {canUseAutosave && (
          <FormControlLabel
            control={
              <Checkbox
                checked={autosaveEnabled}
                onChange={(e) => onAutosaveToggle(e.target.checked)}
                size="small"
              />
            }
            label={t('editor.autosave')}
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'text.secondary',
              },
            }}
          />
        )}

        {mainContentId && (
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={isSaving || !isDirty}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ borderRadius: 2, px: 3 }}
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
